import { Injectable } from '@angular/core';
import { IDynamicExportDataColumn } from '@sharedComponents/tables/models/dynamic-table-export-columns.model';
import { saveAs } from 'file-saver';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root',
})
export class ExportDataService {

  /**
 * Exports the given data as a PDF file.
 * 
 * @param {string} nameFile - The name of the file to be downloaded.
 * @param {string} titleDoc - The title of the document to be displayed in the PDF.
 * @param {Array<IDynamicExportDataColumn>} headColumn - The header columns for the table in the PDF.
 * @param {Array<any>} bodyData - The data to be exported as a PDF file.
 * 
 * @example
 * // Define the header columns and body data
 * const headColumn = [{ title: 'Key', dataKey: 'key' }];
 * const bodyData = [{ key: 'value1' }, { key: 'value2' }];
 * 
 * // Call the exportPdf method to export data as a PDF file
 * exportPdf('myData', 'My Data Title', headColumn, bodyData);
 * 
 * @throws {Error} - Throws an error if there is a problem creating the PDF file or initiating the download.
 */
  exportPdf(nameFile: string, titleDoc: string, headColumn: Array<IDynamicExportDataColumn>, bodyData: Array<any>) {
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then((x) => {
        const auxTable: any = [];
        bodyData.forEach((body) => {
          auxTable.push(Object.values(body));
        });

        const doc = new jsPDF.default('p');
        doc.text(titleDoc, 14, 10);
        autoTable(doc, {
          head: [headColumn],
          body: auxTable,
        });
        doc.save(`${nameFile}.pdf`);
      });
    });
  }

  /**
  * Exports the given data as an Excel file.
  * 
  * @param {any} body - The data to be exported as an Excel file.
  * @param {string} nameFile - The name of the file to be downloaded.
  * 
  * @example
  * // Call the exportExcel method to export data as an Excel file
  * const data = [{ key: 'value1' }, { key: 'value2' }];
  * exportExcel(data, 'myData.xlsx');
  * 
  * @throws {Error} - Throws an error if there is a problem creating the Excel file or initiating the download.
  */
  exportExcel(body: any, nameFile: string) {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(body);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, nameFile);
    });
  }

  /**
   * Exports the given data as a JSON file.
   * 
   * @param {any} data - The data to be exported as a JSON file.
   * @param {string} [nameFile='data.json'] - The name of the file to be downloaded. Default is 'data.json'.
   * 
   * @example
   * // Call the exportJSON method to export data as a JSON file
   * const data = { key: 'value' };
   * exportJSON(data, 'myData.json');
   * 
   * @throws {Error} - Throws an error if there is a problem creating the Blob or initiating the download.
   */
  exportJSON(data: any, nameFile: string = 'data.json') {
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nameFile;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
 * Exporta una tabla HTML a un archivo Excel (.xlsx).
 * 
 * @param {string} elementId - El ID del elemento tabla (<table>) que se desea exportar.
 * @param {string} nameFile - El nombre del archivo a descargar (sin extensión).
 * 
 * @example
 * // Exportar una tabla con id="miTabla"
 * exportService.parseTableToExcel('miTabla', 'Reporte');
 */
parseTableToExcel(elementId: string, nameFile: string): void {
  const tableElement = document.getElementById(elementId) as HTMLTableElement;

  if (!tableElement) {
    console.error(`No se encontró el elemento con id '${elementId}'`);
    return;
  }

  import('xlsx').then((xlsx) => {
    // Clona la tabla
    const clonedTable = tableElement.cloneNode(true) as HTMLTableElement;

    // Limpia celdas ocultas
    clonedTable.querySelectorAll('.display-none-cel, .hidden-cel').forEach(cell => {
      cell.remove();
    });

    // Normaliza los valores numéricos con formato "latino"
    clonedTable.querySelectorAll('td').forEach(cell => {
      const rawText = cell.innerText.trim();

      // Detecta si el contenido parece un número con formato latino
      if (/^-?\d{1,3}(\.\d{3})*(,\d+)?$/.test(rawText)) {
        const normalized = rawText.replace(/\./g, '').replace(',', '.');
        const num = parseFloat(normalized);
        if (!isNaN(num)) {
          cell.innerText = String(num);
        }
      }
    });

    const worksheet = xlsx.utils.table_to_sheet(clonedTable);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, 'Datos');

    const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, nameFile);
  })
}



  private saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION, { autoBom: false });
  }
}
